import { assign, fromPromise, sendTo, setup } from 'xstate';
import { showListingErrorAction } from '../listingConflicts';
import { clearDraft, resetLoading, retainDraft, setLoading } from './actions';
import { Context, Events, OnAddEvent, OnUpdateEvent, Scope } from './types';

export const createListingStepMachine = <T>(
  updateFn: (props: { input: Scope<T> }) => Promise<unknown>,
  parentMachineId: string,
  createFn: (props: { input: Scope<T> }) => Promise<unknown> = updateFn,
  createEventType: 'ON_ADD_LISTING' | 'ON_ADD_DETAIL' = 'ON_ADD_LISTING',
  // [MCV-1523] The create side was already parameterised; the update side was
  // hardcoded to ON_UPDATE_DETAIL, so a basic-info PATCH was merged into
  // `listing.detail` and top-level fields (aoiSpecialtyId, specialtyName) were
  // never refreshed — which is the stale Provider Specialty this ticket is
  // about. Detail steps keep ON_UPDATE_DETAIL; the two basic-info machines pass
  // ON_UPDATE_LISTING so the parent's `updateListing` actually runs.
  updateEventType: 'ON_UPDATE_LISTING' | 'ON_UPDATE_DETAIL' = 'ON_UPDATE_DETAIL'
) => {
  return setup({
    actors: {
      updateListing: fromPromise(updateFn),
      createListing: fromPromise(createFn),
    },
    types: {
      context: {} as Context,
      events: {} as Events<T>,
      input: {} as Pick<Context, 'parentRef'>,
    },
    actions: {
      sendError: showListingErrorAction,
      setLoading: assign(setLoading),
      resetLoading: assign(resetLoading),
      retainDraft: assign(retainDraft),
      clearDraft: assign(clearDraft),
      sendCreatedListing: sendTo(
        ({ context }) => {
          const parentRef = context.parentRef;
          if (!parentRef) {
            throw new Error('parentRef is required in createListingStepMachine');
          }
          return parentRef;
        },
        ({ event }: Scope<T>) => ({ type: createEventType, payload: event as OnAddEvent<T> })
      ),
      updateListing: sendTo(
        ({ context }) => {
          const parentRef = context.parentRef;
          if (!parentRef) {
            throw new Error('parentRef is required in createListingStepMachine');
          }
          return parentRef;
        },
        ({ event }: Scope<T>) => ({ type: updateEventType, payload: event as OnUpdateEvent<T> })
      ),
      sendNextPage: sendTo(
        ({ context }) => {
          const parentRef = context.parentRef;
          if (!parentRef) {
            throw new Error('parentRef is required in createListingStepMachine');
          }
          return parentRef;
        },
        () => ({ type: 'ON_NEXT' })
      ),
    },
  }).createMachine({
    context: ({ input }) => {
      if (!input.parentRef) {
        throw new Error('createListingStepMachine requires parentRef');
      }
      return {
        loading: false,
        parentRef: input?.parentRef,
      };
    },
    initial: '$_VIEW',
    states: {
      $_VIEW: {
        on: {
          ON_ADD: {
            target: '$_SEND_CREATE',
            actions: 'setLoading',
          },
          ON_UPDATE: {
            target: '$_SEND_UPDATE',
            actions: 'setLoading',
          },
          ON_SAVE: {
            target: '$_SEND_SAVE',
            actions: ['setLoading', 'retainDraft'],
          },
          ON_DETAIL_UPDATED: {},
        },
      },
      $_SEND_CREATE: {
        invoke: {
          id: 'createListing',
          src: 'createListing',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: OnAddEvent<T> },
          onDone: {
            target: '$_VIEW',
            actions: ['resetLoading', 'clearDraft', 'sendCreatedListing', 'sendNextPage'],
          },
          onError: {
            target: '$_VIEW',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
      $_SEND_UPDATE: {
        invoke: {
          id: 'updateListing',
          src: 'updateListing',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: OnUpdateEvent<T> },
          onDone: {
            target: '$_VIEW',
            actions: ['resetLoading', 'clearDraft', 'updateListing', 'sendNextPage'],
          },
          onError: {
            target: '$_VIEW',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
      // [MCV-1543] Identical to $_SEND_UPDATE except that it does NOT
      // sendNextPage. A step leaving the screen backwards still has to write
      // what the user typed — routing that through ON_UPDATE would have shoved
      // the wizard forward at the same time.
      $_SEND_SAVE: {
        invoke: {
          id: 'saveListing',
          src: 'updateListing',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: OnUpdateEvent<T> },
          onDone: {
            target: '$_VIEW',
            // The draft is only released once the write for it has landed.
            actions: ['resetLoading', 'updateListing', 'clearDraft'],
          },
          onError: {
            // [MCV-1543] Deliberately keeps pendingDraft. The form unmounted
            // when the flush fired, so this context holds the only remaining
            // copy of the user's edits; dropping it here is the data loss.
            target: '$_VIEW',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
    },
  });
};
